/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings.sections

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_account_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_logout
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_logout_cancel
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_logout_confirm_cta
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_logout_confirm_message
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_logout_confirm_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_version
import studio.oddpods.cauce.feature.settings.getAppVersion

@Composable
internal fun AccountSection(
    onLogoutClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var confirmVisible by rememberSaveable { mutableStateOf(false) }

    Column(modifier = modifier) {
        OutlinedCard(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    text = stringResource(Res.string.feature_settings_account_title),
                    style = MaterialTheme.typography.titleMedium,
                )
                Spacer(Modifier.height(8.dp))
                TextButton(
                    onClick = { confirmVisible = true },
                    colors = ButtonDefaults.textButtonColors(
                        contentColor = MaterialTheme.colorScheme.error,
                    ),
                ) {
                    Text(stringResource(Res.string.feature_settings_logout))
                }
            }
        }

        val version = getAppVersion()

        Text(
            text = stringResource(Res.string.feature_settings_version, version),
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            textAlign = TextAlign.Center,
        )

        if (confirmVisible) {
            AlertDialog(
                onDismissRequest = { confirmVisible = false },
                title = { Text(stringResource(Res.string.feature_settings_logout_confirm_title)) },
                text = { Text(stringResource(Res.string.feature_settings_logout_confirm_message)) },
                confirmButton = {
                    TextButton(
                        onClick = {
                            confirmVisible = false
                            onLogoutClick()
                        },
                    ) {
                        Text(stringResource(Res.string.feature_settings_logout_confirm_cta))
                    }
                },
                dismissButton = {
                    TextButton(onClick = { confirmVisible = false }) {
                        Text(stringResource(Res.string.feature_settings_logout_cancel))
                    }
                },
            )
        }
    }
}
